import type { FC } from 'react';
import { FormProvider, useFormState, type FieldErrors } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { _isEmptyString, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash.ts';
import { defaultPromiseErrorHandler, formatNumber, isWorkflowMeasurement, preventNumberScroll } from '@/helpers.tsx';
import { useDevices } from '@/utils/devices/useDevices.tsx';
import type {
  MeasurementFieldProps,
  MeasurementFormData,
} from '@/components/Subjects/Show/Measurements/MeasurementWorkflow.model.tsx';
import NumericDelta from '@/components/UI/NumericDelta';
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange.tsx';

const MeasurementField: FC<MeasurementFieldProps> = ({
  index,
  calc,
  editing,
  defaultValues,
  submitting,
  disabled,
  handleKeyPress,
  formulas,
  showDifference,
  handleDifference,
  assignAltId,
  formMethods,
  fieldWatch,
  onSubmit,
  playErrorSound,
  playNotifySound,
  allMeasurementFieldsFilled,
  cursorPosition,
}) => {
  const { handleSubmit, register, setValue, getValues, formState, trigger, watch } = formMethods;
  const { errors, isSubmitting } = formState;

  const isWeight = calc.id === 'weight';
  const isWeightNotAllRequired = (isWeight && calc.measurements_not_always_required) ?? false;

  const checkMeasurementComplete = () => {
    const { fields } = getValues();
    const complete = Object.values(fields).every((field) => _isNotEmpty(field));

    if (complete && !isSubmitting) {
      handleSubmit(onSubmit)().catch(defaultPromiseErrorHandler);
    }
  };

  const { errors: errorState } = useFormState(formMethods);
  const onNewReading = (response: string) => {
    if (!editing && allMeasurementFieldsFilled) {
      // If there are no measurements to be captured no need to proceed further.
      return;
    }

    calc.measurements?.some((measurement) => {
      const measurementValue = getValues(`fields.${measurement.id}`);
      if (_isEmptyString(measurementValue)) {
        setValue(`fields.${measurement.id}`, response);
        trigger(`fields.${measurement.id}`).then(() => {
          const errors = errorState as FieldErrors<MeasurementFormData>;
          if (_notNil(errors?.fields?.[measurement.id])) {
            playErrorSound();
          } else {
            playNotifySound();
          }
        });
        return true;
      }
      return false;
    });

    const isReadingComplete = !(
      calc.measurements?.some((measurement) => {
        const measurementValue = getValues(`fields.${measurement.id}`);
        return _isEmptyString(measurementValue);
      }) ?? false
    );
    nextReading(isReadingComplete);
    checkMeasurementComplete();
  };
  const { nextReading } = useDevices({
    targetId: calc.id,
    onNewReading,
    onDeviceError: playErrorSound,
  });

  return (
    <FormProvider {...formMethods}>
      <div className="bb b--moon-gray pv3" key={index}>
        {calc?.measurements?.map((m, i) => {
          const isDisabled = (!editing && Boolean(defaultValues[m.id])) || submitting || disabled === true;
          const fieldAlwaysRequired =
            calc.measurements_not_always_required !== true &&
            (_isNil(calc.parent_dependency_calculations_not_required) ||
              calc.parent_dependency_calculations_not_required !== true);
          const focus = _notNil(cursorPosition)
            ? calc.id === cursorPosition && calc.measurements?.[0]?.id === m.id
            : assignAltId !== true && index === 0 && i === 0;

          return (
            <div className="pb3" key={i}>
              <label>
                {m.name} {_isEmptyString(m?.unit ?? '') ? '' : `(${m.unit})`}
              </label>
              <input
                autoFocus={focus}
                type="number"
                onWheel={preventNumberScroll}
                step="any"
                {...register(`fields.${m.id}`, {
                  validate: (value: string) => {
                    if (!_isEmptyString(value) && !isWorkflowMeasurement(value)) {
                      return 'Must be a valid number';
                    }

                    const validateFieldWatch = watch('fields');
                    if (Object.values(validateFieldWatch).every((val) => val === '')) {
                      return 'At least one value must be entered';
                    }

                    if (fieldAlwaysRequired && _isEmptyString(validateFieldWatch[m.id])) {
                      return 'This field is required';
                    }
                    const weightLessThanOrEqualToZero = isWeight && !isWeightNotAllRequired && Number(value) <= 0;
                    const weightNotAllRequiredLessThanOrEqualToZero =
                      isWeightNotAllRequired && value !== '' && Number(value) <= 0;

                    if (weightLessThanOrEqualToZero || weightNotAllRequiredLessThanOrEqualToZero) {
                      return 'Must be greater than 0';
                    }

                    if (
                      _isEmptyString(validateFieldWatch[m.id]) &&
                      calc.measurements_not_always_required !== true &&
                      calc.parent_dependency_calculations_not_required === true
                    ) {
                      const calcFields: Array<string> = Object.entries(validateFieldWatch)
                        .filter(([key]) => calc?.input_list?.includes(key) ?? false)
                        .map(([, value]) => value as string);
                      const partialRecording =
                        calcFields.some((input: string) => _isEmptyString(input)) &&
                        calcFields.some((input: string) => !_isEmptyString(input));

                      if (partialRecording) {
                        return 'This field is required';
                      }
                    }
                    return true;
                  },
                })}
                disabled={isDisabled}
                className={cn({ input__error: _notNil(errors?.fields?.[m.id]) })}
                onKeyDown={handleKeyPress}
                style={{ marginBottom: 0 }}
                data-testid={`workflow-measurement-fields-${m.id}`}
              />
              <ErrorMessage
                errors={errors}
                name={`fields.${m.id}`}
                render={({ message }) => (
                  <p className="f6 red db lh-copy pt1" data-testid={`workflow-measurement-fields-error-${m.id}`}>
                    {message}
                  </p>
                )}
              />
            </div>
          );
        })}
        <div className="pt2">
          <label>
            {calc.name} {_isNotBlank(calc.unit) && `(${calc.unit})`}
          </label>
          <div className="flex items-center">
            <h3
              data-tooltip-id="global-tooltip-id"
              data-tooltip-content={calc.formula}
              data-testid={`workflow-measurement-${calc.id}-calculation`}
              className="f3 normal near-black pa2 ph3 lh-title bg-near-white br2 dib"
            >
              {_notNil(formulas[calc.id]) && !Number.isNaN(formulas[calc.id]) && formulas[calc.id] !== 0
                ? formatNumber(formulas[calc.id])
                : '-'}
            </h3>
            {showDifference(calc.id) && (
              <NumericDelta
                value={_isNotBlank(fieldWatch[calc.id]) ? fieldWatch[calc.id] : formulas[calc.id]}
                valueChange={String(handleDifference(calc?.id))}
                percentageOnly={handleDifference(calc.id) === 0}
              />
            )}
          </div>
        </div>
        {editing && <ReasonForChange />}
      </div>
    </FormProvider>
  );
};

export default MeasurementField;
